import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'

const pillars = [
  {
    title: 'Sistema Nervoso',
    desc: 'Gestione di stress, energia, recupero e attivazione.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-10 h-10">
        <path d="M12 2a10 10 0 100 20 10 10 0 000-20z" />
        <path d="M12 6v6l4 2" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
  {
    title: 'Movimento',
    desc: 'Corpo più presente, postura più solida, più fiducia fisica.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-10 h-10">
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    ),
  },
  {
    title: 'Focus',
    desc: 'Meno caos mentale. Più direzione.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-10 h-10">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
      </svg>
    ),
  },
  {
    title: 'Azione',
    desc: 'Micro missioni concrete. Niente teoria infinita.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-10 h-10">
        <path d="M5 12h14M12 5l7 7-7 7" />
      </svg>
    ),
  },
]

export default function MethodSection() {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })

  return (
    <section
      id="metodo"
      ref={sectionRef}
      className="relative w-full py-24 md:py-32 px-4"
      style={{ background: '#050510' }}
    >
      {/* Subtle background accent */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, rgba(139,92,246,0.06) 0%, transparent 60%)',
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-nerd-white mb-4">
            Non motivazione.{' '}
            <span className="text-gradient">Sistema.</span>
          </h2>
          <p className="text-nerd-gray text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Nerd Coach lavora su mente, corpo e comportamento. Non ti chiede di diventare qualcun altro. Ti aiuta a smettere di usare il tuo mondo come nascondiglio.
          </p>
        </motion.div>

        {/* Pillars */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {/* Connecting lines (desktop only) */}
          <div className="hidden lg:block absolute top-1/2 left-[12.5%] right-[12.5%] h-[1px]">
            <div
              className="w-full h-full"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(139,92,246,0.3), rgba(0,212,255,0.3), transparent)',
              }}
            />
          </div>

          {pillars.map((pillar, i) => (
            <motion.div
              key={pillar.title}
              className="nerd-card rounded-xl p-8 text-center relative group"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Number badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-nerd-bg border border-nerd-purple/40 flex items-center justify-center">
                <span className="text-[10px] text-nerd-purple font-display font-bold">{String(i + 1).padStart(2, '0')}</span>
              </div>

              <div className="text-nerd-purple mb-4 flex justify-center">
                {pillar.icon}
              </div>

              <h3 className="font-display text-xl font-semibold text-nerd-white mb-3">
                {pillar.title}
              </h3>

              <p className="text-nerd-gray text-sm leading-relaxed">
                {pillar.desc}
              </p>

              {/* Bottom glow */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-[1px] bg-gradient-to-r from-transparent via-nerd-purple/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
